/**
 * Vertical clip: the round as a finished 9:16 film (2026-09-09).
 *
 * Playback puts a recorded round back on the beamer. A clip is the other
 * direction: the same recording cut for a phone — a portrait frame that
 * needs no camera, no editor and no footage, and can go straight onto
 * Reels, TikTok or Shorts.
 *
 * Two problems separate a clip from a replay, and both are solved here
 * rather than in the view:
 *
 *  1. **Length.** A round runs seven to fourteen minutes and spends much of
 *     it in silence while someone thinks. A clip has about a minute of
 *     patience. So the round is *time-mapped*: stretches where something
 *     happens play at (or near) their own pace, and every silence longer
 *     than a threshold is compressed to a held beat — the clocks visibly
 *     jump, which reads as a time-lapse rather than as a cut.
 *  2. **Determinism.** The clip is rendered frame by frame by stepping a
 *     playhead (see scripts/render-clip.mts), so the mapping from output
 *     time to recording time has to be a pure function. Nothing here may
 *     depend on a wall clock — which is also why the browser preview and
 *     the rendered file are guaranteed to be the same film.
 *
 * The layout is authored at a fixed 1080 × 1920 and scaled to whatever the
 * target size is, so the preview in the browser is the render.
 */

import { PREROLL_MS, roundGaps, finalState, type PlaybackRecording } from './playback';
import { countWords, type AuthorSeat } from './types';

/** The frame the clip is authored in; every size is a scale of this. */
export const CLIP_WIDTH = 1080;
export const CLIP_HEIGHT = 1920;

/** The title card: NAME VS NAME, before the task slide. */
export const CLIP_VERSUS_MS = 2_400;
/** The task slide — together with the versus card this is the pre-roll. */
export const CLIP_TASK_MS = PREROLL_MS - CLIP_VERSUS_MS;
/** The end card: ENDE, then the round's numbers and where to find more. */
export const CLIP_OUTRO_MS = 5_600;

export type ClipSection = 'versus' | 'task' | 'round' | 'outro';

export interface ClipOptions {
  /** Pace of the stretches where something happens. 1 = as it was played. */
  speed: number;
  /** A silence longer than this is compressed (0 disables compression). */
  idleThresholdMs: number;
  /** …to this. The clocks jump; the beat stays. */
  idleHoldMs: number;
  /** Title card + task slide at the head. */
  intro: boolean;
  /** ENDE + numbers at the tail. */
  outro: boolean;
  /** An excerpt of the round, in round time — for a highlight clip. */
  range: { fromMs: number; toMs: number } | null;
}

export const DEFAULT_CLIP_OPTIONS: ClipOptions = {
  speed: 1,
  idleThresholdMs: 2_500,
  idleHoldMs: 700,
  intro: true,
  outro: true,
  range: null,
};

/** One stretch of the round, and where it sits in the finished clip. */
interface Span {
  roundFrom: number;
  roundTo: number;
  outFrom: number;
  outTo: number;
  idle: boolean;
}

export interface ClipPlan {
  options: ClipOptions;
  /** Excerpt of the round the clip covers, in round time. */
  roundFromMs: number;
  roundToMs: number;
  introMs: number;
  outroMs: number;
  /** Output length of the round portion, after time mapping. */
  roundOutMs: number;
  /** Length of the finished clip. */
  outDurationMs: number;
  /** How much real round time was compressed away. */
  compressedMs: number;
  spans: Span[];
}

export function planClip(rec: PlaybackRecording, partial: Partial<ClipOptions> = {}): ClipPlan {
  const options: ClipOptions = { ...DEFAULT_CLIP_OPTIONS, ...partial };
  const speed = options.speed > 0 ? options.speed : 1;
  const roundFromMs = Math.max(0, Math.min(options.range?.fromMs ?? 0, rec.roundMs));
  const roundToMs = Math.max(roundFromMs, Math.min(options.range?.toMs ?? rec.roundMs, rec.roundMs));

  const gaps =
    options.idleThresholdMs > 0
      ? roundGaps(rec, options.idleThresholdMs)
          .map((g) => ({ from: Math.max(g.from, roundFromMs), to: Math.min(g.to, roundToMs) }))
          .filter((g) => g.to - g.from > options.idleThresholdMs)
      : [];

  const spans: Span[] = [];
  let round = roundFromMs;
  let out = 0;
  let compressedMs = 0;

  const push = (roundFrom: number, roundTo: number, outMs: number, idle: boolean) => {
    if (roundTo <= roundFrom || outMs <= 0) return;
    spans.push({ roundFrom, roundTo, outFrom: out, outTo: out + outMs, idle });
    out += outMs;
  };

  for (const gap of gaps) {
    push(round, gap.from, (gap.from - round) / speed, false);
    push(gap.from, gap.to, options.idleHoldMs, true);
    compressedMs += Math.max(0, gap.to - gap.from - options.idleHoldMs);
    round = gap.to;
  }
  push(round, roundToMs, (roundToMs - round) / speed, false);

  const introMs = options.intro ? PREROLL_MS : 0;
  const outroMs = options.outro ? CLIP_OUTRO_MS : 0;
  return {
    options,
    roundFromMs,
    roundToMs,
    introMs,
    outroMs,
    roundOutMs: out,
    outDurationMs: introMs + out + outroMs,
    compressedMs,
    spans,
  };
}

/**
 * Output time → playback playhead. This is the whole contract between the
 * clip and the recording: everything the frame shows is `stateAt()` of the
 * number this returns.
 */
export function clipPlayhead(plan: ClipPlan, outMs: number): number {
  if (plan.options.intro && outMs < plan.introMs) return Math.max(0, outMs);
  const o = outMs - plan.introMs;
  if (o <= 0) return PREROLL_MS + plan.roundFromMs;
  for (const span of plan.spans) {
    if (o < span.outTo) {
      const share = (o - span.outFrom) / (span.outTo - span.outFrom);
      return PREROLL_MS + span.roundFrom + share * (span.roundTo - span.roundFrom);
    }
  }
  return PREROLL_MS + plan.roundToMs;
}

export function clipSection(plan: ClipPlan, outMs: number): ClipSection {
  if (plan.options.intro) {
    if (outMs < CLIP_VERSUS_MS) return 'versus';
    if (outMs < plan.introMs) return 'task';
  }
  return outMs < plan.introMs + plan.roundOutMs ? 'round' : 'outro';
}

/** Frames a render produces, so the script and the tests agree on the count. */
export function clipFrameCount(plan: ClipPlan, fps: number): number {
  return Math.max(1, Math.round((plan.outDurationMs / 1000) * fps));
}

/** Output time of frame `n` — the render's only clock. */
export function clipFrameTime(fps: number, frame: number): number {
  return (frame * 1000) / fps;
}

/* -------------------------------------------------------------- numbers - */

export interface ClipAuthorStat {
  seat: AuthorSeat;
  chars: number;
  words: number;
  /** Share of the round's characters, 0..1 — the bar on the end card. */
  share: number;
}

export interface ClipStats {
  chars: number;
  words: number;
  /** Characters per minute over the round's real length. */
  charsPerMinute: number;
  keystrokes: number;
  /** Keys the no-delete rule swallowed — the evening's quiet drama. */
  blocked: number;
  turns: number;
  jokers: number;
  penalties: number;
  authors: ClipAuthorStat[];
}

/** The end card's numbers, read off the finished round. */
export function clipStats(rec: PlaybackRecording): ClipStats {
  const round = finalState(rec).round;
  const chars = round.text.length;
  const authors = rec.authors.map((seat) => {
    let own = '';
    for (const [i, segment] of round.segments.entries()) {
      if (segment.author !== seat.index) continue;
      own += round.text.slice(segment.start, round.segments[i + 1]?.start ?? round.text.length);
    }
    return {
      seat,
      chars: own.length,
      words: countWords(own),
      share: chars === 0 ? 0 : own.length / chars,
    };
  });
  return {
    chars,
    words: countWords(round.text),
    charsPerMinute: rec.roundMs === 0 ? 0 : Math.round(chars / (rec.roundMs / 60_000)),
    keystrokes: rec.keystrokeCount,
    blocked: rec.blockedCount,
    turns: rec.markers.filter((m) => m.kind === 'pass').length,
    jokers: rec.markers.filter((m) => m.kind === 'joker').length,
    penalties: rec.markers.filter((m) => m.kind === 'penalty').length,
    authors,
  };
}

/**
 * Annotations for the portrait frame. A 1080-wide column has no room for the
 * projection's left margin, so a joker, a penalty or a timeout arrives as a
 * chip under the clocks and stays for a few seconds — which is also how a
 * phone viewer expects to be told that something happened.
 */
export const CLIP_CHIP_MS = 3_400;

export function clipChips(rec: PlaybackRecording, playheadMs: number) {
  return rec.markers.filter(
    (m) =>
      (m.kind === 'joker' || m.kind === 'penalty' || m.kind === 'bonus' || m.kind === 'frozen') &&
      playheadMs >= m.atMs &&
      playheadMs < m.atMs + CLIP_CHIP_MS,
  );
}
